import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoPanel } from '../components/VideoPanel';
import { ProductsPanel } from '../components/ProductsPanel';
import { ProductSuggestionFocus } from '../components/ProductSuggestionFocus';
import { ChatOverlay } from '../components/ChatOverlay';
import { CartOverlay } from '../components/CartOverlay';
import { WishlistOverlay } from '../components/WishlistOverlay';
import { ProductDetailOverlay } from '../components/ProductDetailOverlay';
import { ModeSwitcher } from '../components/ModeSwitcher';
import { ConnectionState } from '../components/ConnectionState';
import { ToastStack } from '../components/Toast';
import { ReconnectionOverlay } from '../components/ReconnectionOverlay';
import { DegradationPrompt } from '../components/DegradationPrompt';
import { MediaErrorPrompt } from '../components/MediaErrorPrompt';
import { getMediaErrorTitle } from '../lib/mediaErrorCopy';
import { EndCallConfirmation } from '../components/EndCallConfirmation';
import { RecordingIndicator } from '../components/RecordingIndicator';
import { useVideoSession, useChatSession } from '../context/liveSession';
import { useReconnection } from '../hooks/useReconnection';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../hooks/useCart';
import { useWishlist } from '../hooks/useWishlist';
import { useProductDetail } from '../hooks/useProductDetail';
import { usePortalWebSocket } from '../hooks/usePortalWebSocket';
import { useToast } from '../hooks/useToast';
import { useNetworkQuality } from '../hooks/useNetworkQuality';
import { useDeviceSelection } from '../hooks/useDeviceSelection';
import { useCaptureRetry } from '../hooks/useCaptureRetry';
import { logger } from '../services/logger';
import { initTwilioDiag } from '../lib/twilio-diag';
import { normalizeProducts, chatProductToProduct } from '../lib/normalizeProducts';
import { buildProductCard } from '../lib/productCard';
import { apiClient } from '../services/api';
import { env } from '../config/env';
import type { CommunicationMode, WishlistItem } from '../types/app';
import type { TwilioCredentials } from '../types/twilio';
import type { SellerInfo, OrganizationInfo, Product, CustomerSession, ProductDetail, ProductDetailVariant, WidgetAuth } from '../types/api';
import type { VideoProviderConfig, ChatProductItem } from '../providers/types';

const MAX_RECONNECT_ATTEMPTS = 3;

/**
 * Append products from `incoming` that are not already present (by id) in `base`.
 * Returns `base` unchanged when there is nothing new, so referential identity is
 * preserved and dependent memos/effects don't re-run needlessly.
 */
function mergeProductsById(base: Product[], incoming: Product[]): Product[] {
  const seen = new Set(base.map((p) => p.id));
  const fresh = incoming.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
  return fresh.length > 0 ? [...base, ...fresh] : base;
}

interface ConsultationScreenProps {
  credentials: TwilioCredentials;
  seller: SellerInfo | null;
  organization: OrganizationInfo;
  initialMode: CommunicationMode;
  customerSession?: CustomerSession;
  callId: number; // SPEC-032: needed for basket API calls
  recordingEnabled: boolean; // SPEC-026/028: Portal has transcription configured for this call
  /**
   * Q1: what this customer answered about having their own voice recorded — true,
   * false, or null when the question never got an answer. Kept apart from
   * `recordingEnabled` because the seller is recorded either way: the pair is what
   * lets the badge say which of the two is true (see RecordingIndicator).
   */
  recordingConsent: boolean | null;
  onEndCall: () => void;
  /**
   * Reports the mode the customer is actually in, on every change. App persists it
   * so a mid-meeting reload resumes here instead of restarting the funnel.
   */
  onModeChange?: (mode: CommunicationMode) => void;
}

/**
 * Determine the suggested fallback mode for degradation.
 */
function getSuggestedMode(currentMode: CommunicationMode): CommunicationMode | null {
  switch (currentMode) {
    case 'video':
      return 'audio';
    case 'audio':
      return 'chat';
    default:
      return null; // chat has no further fallback
  }
}

export function ConsultationScreen({
  credentials,
  seller,
  organization,
  initialMode,
  customerSession,
  callId,
  recordingEnabled,
  recordingConsent,
  onEndCall,
  onModeChange,
}: ConsultationScreenProps) {
  const [currentSeller, setCurrentSeller] = useState<SellerInfo | null>(seller);
  const [currentMode, setCurrentMode] = useState<CommunicationMode>(initialMode);
  const [isChatOpen, setIsChatOpen] = useState(initialMode === 'chat');
  const [showDegradation, setShowDegradation] = useState(false);
  const [showMediaErrorPrompt, setShowMediaErrorPrompt] = useState(false);
  const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);
  const [showProducts, setShowProducts] = useState(true);
  const [chatHasUnread, setChatHasUnread] = useState(false);
  const prevChatMsgCountRef = useRef(0);
  const viewedProductIdsRef = useRef<Set<number>>(new Set());

  // Target mode of a lazy connect that failed before a room existed. The recovery
  // handlers need it because `currentMode` is rolled back on failure and therefore
  // no longer says what the customer asked for. Null once a room is established.
  const pendingModeRef = useRef<CommunicationMode | null>(null);
  // Target mode of a switch inside an existing room whose device could not be
  // acquired — a voice customer whose camera the browser refused on the way to
  // video. The room is fine, so the recovery is to ask for that device again,
  // never to reconnect. Null once a switch has gone through.
  const pendingToggleModeRef = useRef<CommunicationMode | null>(null);
  // Set once the customer has settled a capture failure by taking a fallback.
  // The video.error effects consult it so the fallback's *own* failure cannot
  // re-open the modal the customer just dismissed. Cleared whenever the customer
  // explicitly asks for a mode again — that is a fresh attempt.
  const captureErrorSettledRef = useRef(false);

  // IB-004 / Stream F: in-call soft-focus for advisor-suggested products.
  // Suggestions arrive as Twilio Conversations product messages (same payload
  // RichMessage renders). We surface them in the right sidebar WITHOUT switching
  // to chat mode, so camera/mic and the VideoPanel stay live.
  // - suggestedProducts: seller-suggested products, merged into the products list.
  // - latestSuggestion: the most recent suggestion shown in the focus card.
  // - isSuggestionExpanded: auto-opened on each new suggestion (Q3).
  // - suggestionPulseKey: bumps on each new suggestion to replay the gold cue.
  const [suggestedProducts, setSuggestedProducts] = useState<Product[]>([]);
  const [latestSuggestion, setLatestSuggestion] = useState<ChatProductItem | null>(null);
  const [isSuggestionExpanded, setIsSuggestionExpanded] = useState(false);
  const [suggestionPulseKey, setSuggestionPulseKey] = useState(0);
  // Twilio message ids already routed, so re-renders / history loads don't re-fire.
  const processedSuggestionIdsRef = useRef<Set<string>>(new Set());
  // Guards the baseline pass (runs once historyLoaded flips true): messages
  // present at history-load are seeded into the list but must NOT auto-open the
  // focus or replay the cue. All later messageAdded events are treated as live.
  const suggestionsBaselinedRef = useRef(false);

  // Construct WidgetAuth from customerSession (SPEC-041c)
  // Uses customerSession.service_id (VeceDb::Service.id) for Widget API signature,
  // NOT organization.id (Organization.id) — they are different tables.
  // Memoized to prevent infinite re-fetch loops in useProducts/useComboProducts.
  const auth: WidgetAuth | null = useMemo(
    () => customerSession
      ? {
          accessToken: customerSession.access_token,
          refreshToken: customerSession.refresh_token,
          serviceId: customerSession.service_id,
        }
      : null,
    [customerSession?.access_token, customerSession?.refresh_token, customerSession?.service_id]
  );

  // Toast system
  const { toasts, addToast, removeToast } = useToast();

  // The one live session of this visit, opened above the screens — see
  // LiveSessionProvider. TransitionScreen has usually already joined the room on it.
  const video = useVideoSession();
  const chat = useChatSession();

  // Device selection — used by MediaErrorPrompt's "Change camera" action
  const deviceSelection = useDeviceSelection(video.isConnected);

  // Network quality
  const networkQuality = useNetworkQuality(video);

  // Products and cart
  // Pass callId so the backend can filter products to the Look attached to this call
  const { products, isLoading: productsLoading, refetch: refetchProducts } = useProducts(auth, undefined, callId);
  const cart = useCart(
    customerSession
      ? {
          callId,
          auth: {
            accessToken: customerSession.access_token,
            serviceId: customerSession.service_id,
          },
        }
      : undefined
  );

  // Wishlist (SPEC-033)
  const wishlist = useWishlist(
    customerSession
      ? {
          callId,
          auth: {
            accessToken: customerSession.access_token,
            serviceId: customerSession.service_id,
          },
        }
      : undefined
  );

  // SPEC-053: per-card loading state for Reserve button
  const [reservingProductId, setReservingProductId] = useState<number | null>(null);

  // QA2/BUG-1: in-flight guard for the Featured-Products heart when it has to
  // resolve a variant before adding. A ref (not state) — this guards a duplicate
  // request, it does not drive any rendering.
  const favoritingProductRef = useRef<number | null>(null);

  // Set of variant IDs currently in cart — used by WishlistOverlay to disable Reserve
  // for items already reserved, and show "In Cart" label instead.
  const cartVariantIdSet = useMemo(
    () => new Set(cart.items.map((item) => item.productVariant.id)),
    [cart.items]
  );

  // Products shown in the panel = the call's Look (from useProducts) plus any
  // advisor-suggested products not already in that list (Q3: auto-add). Suggested
  // extras are prepended so a fresh suggestion is easy to find; the Look order is
  // otherwise preserved. All product handlers below resolve against this merged
  // list so a suggested-only product still works in the PDP / cart / wishlist.
  const displayProducts = useMemo(() => {
    if (suggestedProducts.length === 0) return products;
    const existing = new Set(products.map((p) => p.id));
    const extras = suggestedProducts.filter((p) => !existing.has(p.id));
    return extras.length > 0 ? [...extras, ...products] : products;
  }, [products, suggestedProducts]);

  // Product detail (SPEC-034)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [pdpInitialVariantId, setPdpInitialVariantId] = useState<number | undefined>(undefined);
  // Counts chat-card clicks that name a variant, so clicking the same card again
  // brings the PDP back to that variant after the customer changed the options.
  const [pdpVariantRequest, setPdpVariantRequest] = useState(0);
  const productDetail = useProductDetail(auth);

  // Handle product click from ProductsPanel (SPEC-034)
  const handleProductClick = useCallback((product: Product) => {
    // Close other overlays (mutual exclusion)
    if (cart.isOpen) cart.toggleCart();
    if (wishlist.isOpen) wishlist.toggleWishlist();

    setSelectedProductId(product.id);
    setPdpInitialVariantId(undefined);
    productDetail.fetchProduct(product.id);

    if (auth && !viewedProductIdsRef.current.has(product.id)) {
      viewedProductIdsRef.current.add(product.id);
      apiClient.markProductViewed(callId, product.id, auth).catch(() => {});
    }
  }, [cart, wishlist, productDetail, auth, callId]);

  // Handle product click from chat product cards (SPEC-039)
  // Opens PDP on the card's variant when it names one (an id the product does not
  // have falls back to the first variant in the PDP); chat stays open.
  const handleChatProductClick = useCallback((productId: number, variantId?: number) => {
    if (cart.isOpen) cart.toggleCart();
    if (wishlist.isOpen) wishlist.toggleWishlist();

    setSelectedProductId(productId);
    setPdpInitialVariantId(variantId);
    if (variantId !== undefined) setPdpVariantRequest((n) => n + 1);
    productDetail.fetchProduct(productId);

    if (auth && !viewedProductIdsRef.current.has(productId)) {
      viewedProductIdsRef.current.add(productId);
      apiClient.markProductViewed(callId, productId, auth).catch(() => {});
    }
  }, [cart, wishlist, productDetail, auth, callId]);

  // IB-004 / Stream F: open the suggested product's PDP from the focus card.
  // Reuses the chat-product path — opens the in-panel PDP overlay, never changes
  // currentMode, so audio/video keep running.
  const handleSuggestionViewDetails = useCallback((productId: number, variantId?: number) => {
    handleChatProductClick(productId, variantId);
  }, [handleChatProductClick]);

  const handleSuggestionToggle = useCallback(() => {
    setIsSuggestionExpanded((v) => !v);
  }, []);

  // Handle product click from cart or wishlist overlay items (SPEC-038)
  // Closes the source overlay (mutual exclusion) and opens PDP with the variant pre-selected.
  const handleOverlayItemClick = useCallback((productId: number, variantId: number) => {
    if (cart.isOpen) cart.toggleCart();
    if (wishlist.isOpen) wishlist.toggleWishlist();

    setSelectedProductId(productId);
    setPdpInitialVariantId(variantId);
    productDetail.fetchProduct(productId);

    if (auth && !viewedProductIdsRef.current.has(productId)) {
      viewedProductIdsRef.current.add(productId);
      apiClient.markProductViewed(callId, productId, auth).catch(() => {});
    }
  }, [cart, wishlist, productDetail, auth, callId]);

  // Handle PDP close
  const handleProductDetailClose = useCallback(() => {
    setSelectedProductId(null);
    setPdpInitialVariantId(undefined);
    productDetail.reset();
  }, [productDetail]);

  // Handle add-to-bag from PDP (uses selected variant ID, quantity, and optional product options)
  const handlePdpAddToBag = useCallback((productId: number, variantId: number, quantity: number, selectedProductOptions?: Record<string, string>) => {
    const product = displayProducts.find(p => p.id === productId);
    if (product) {
      // Return the promise so the PDP's in-flight guard (QA2/BUG-2) actually
      // spans the POST, not just the synchronous dispatch.
      return cart.addItem(product, { variantId }, quantity, selectedProductOptions);
    }
  }, [displayProducts, cart]);

  /**
   * Handle combo "Add Bundle to Bag" from PDP (SPEC-036).
   * Maps combo items to cart.addComboItems format.
   * AC-16: one entry per combo product.
   * AC-17: discount passed as number (from parent's combo_product_discount).
   * AC-19: main product NOT added — only child variant IDs passed.
   */
  const handlePdpAddComboBag = useCallback(
    async (items: Array<{ productId: number; variantId: number; discount: number }>) => {
      await cart.addComboItems(
        items.map((item) => ({
          product: { id: item.productId, title: '', price: '', image_url: '' } as Product,
          variantId: item.variantId,
          discountVal: item.discount,
        }))
      );
    },
    [cart]
  );

  // Handle toggle favorite from PDP.
  // SPEC-064: variant-aware. PDP sources variantId from its selectedVariant and
  // threads it through so sibling variants of the same product toggle independently.
  const handlePdpToggleFavorite = useCallback((productId: number, variantId: number) => {
    const product = displayProducts.find(p => p.id === productId);
    if (product) {
      wishlist.toggleItem(product, variantId);
    }
  }, [displayProducts, wishlist]);

  // Handle toggle favorite from ProductsPanel (product cards).
  // SPEC-064: ProductsPanel has no variant context at the card level (card-level
  // wildcard semantics — see SPEC-064 R5). We resolve a variantId to thread to
  // wishlist.toggleItem by checking whether ANY variant of this product is
  // already wishlisted; if so we remove that entry; otherwise we add using the
  // product id as a synthetic key matching the server's default-variant path.
  const handleProductsPanelToggleFavorite = useCallback(
    async (product: Product) => {
      const existing = wishlist.items.find((item) => item.product.id === product.id);

      // Toggling OFF: reuse the already-wishlisted variant id so removal matches
      // the correct record.
      if (existing) {
        await wishlist.toggleItem(product, existing.productVariant.id);
        return;
      }

      // Toggling ON with a catalog product: API normalization gives Look products
      // a product_code (see api.ts normalizeProduct), so addItem's remote_obj_id
      // path identifies it. Pass 0 — the "no variant context" sentinel.
      if (product.product_code) {
        await wishlist.toggleItem(product, 0);
        return;
      }

      // QA2/BUG-1 — Toggling ON a chat-SUGGESTED product. chatProductToProduct
      // (lib/normalizeProducts.ts) builds these from an AI payload with only
      // id/title/price/image_url — no product_code — so the remote_obj_id path
      // has nothing to send and addWishProduct would reject the call.
      // We deliberately do NOT synthesize product_code from product.id: it is
      // unverified whether a chat payload's id is the Shopify remote product id,
      // and a wrong identifier would favourite the WRONG product. Instead resolve
      // a REAL variant, the same way handleReserve does for these same products.
      if (!auth) return;
      // Guard the resolution round-trip against double-clicks so we don't
      // issue two adds for the same card.
      if (favoritingProductRef.current === product.id) return;
      favoritingProductRef.current = product.id;
      try {
        const detail = await apiClient.getProductDetail(product.id, auth);
        const variants = detail.response?.product_variants ?? [];
        if (variants.length === 0) {
          throw new Error('No variants returned for product');
        }
        await wishlist.toggleItem(product, variants[0].id);
      } catch (err) {
        // Resolution failed (network / no variant). Nothing optimistic was
        // painted — addItem is never reached — so the heart simply stays
        // unfilled; surface the same failure toast the add path uses.
        logger.error('handleProductsPanelToggleFavorite: variant resolution failed', {
          productId: product.id,
          error: err,
        });
        addToast({
          type: 'error',
          title: 'Failed to add to wishlist',
          message: 'Could not add this item, please try again',
        });
      } finally {
        favoritingProductRef.current = null;
      }
    },
    [wishlist, auth, addToast]
  );

  // Handle share product from PDP (SPEC-037)
  // Sends a product card message to the active chat conversation using Variant A format.
  const handlePdpShare = useCallback(async (product: ProductDetail, variant: ProductDetailVariant | null) => {
    // A one-card array: Portal and every client read both an array and a single object.
    const card = buildProductCard(product, variant);
    const attributes = {
      type: 'product',
      json: JSON.stringify([card]),
    };

    try {
      await chat.sendMessage('', attributes);
      // Only in chat mode, where the overlay IS the consultation. ChatOverlay is
      // drawn inside the video area (see its mount below), so opening it during an
      // audio or video call drops a panel over the advisor's picture — for an
      // action the toast right below already confirms. Sharing a product is not a
      // request to leave the call.
      if (currentMode === 'chat') {
        setIsChatOpen(true);
      }
      addToast({
        type: 'success',
        title: 'Product Shared',
        message: `${card.name || 'Product'} sent to chat`,
        duration: 3000,
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Share Failed',
        message: 'Could not share product to chat. Please try again.',
      });
    }
  }, [chat, addToast, currentMode]);

  // Thread cart-qty updates from PDP back to the single useCart instance owned here.
  // PDP debounces calls to this handler (~400ms) so rapid ± clicks collapse into one.
  const handlePdpQuantityChange = useCallback(
    (productId: number | string, variantId: number | string, newQty: number) => {
      const entry = cart.findInCart(productId as number, variantId as number);
      if (!entry) return;
      cart.updateQuantity(entry.id, variantId as number, newQty);
    },
    [cart.findInCart, cart.updateQuantity]
  );

  // Mutual exclusion: only one overlay open at a time
  const handleCartClick = useCallback(() => {
    if (wishlist.isOpen) wishlist.toggleWishlist();
    if (selectedProductId) handleProductDetailClose(); // Close PDP
    cart.toggleCart();
  }, [wishlist, cart, selectedProductId, handleProductDetailClose]);

  const handleWishlistClick = useCallback(() => {
    if (cart.isOpen) cart.toggleCart();
    if (selectedProductId) handleProductDetailClose(); // Close PDP
    wishlist.toggleWishlist();
  }, [cart, wishlist, selectedProductId, handleProductDetailClose]);

  // Portal WebSocket for Immerss events with connection change toast.
  // Suppress the "Connection Restored" toast on the initial connect — it's only
  // meaningful after we've actually seen a disconnect.
  const wsEverDisconnectedRef = useRef(false);
  const handleWsConnectionChange = useCallback((connected: boolean) => {
    if (connected) {
      if (wsEverDisconnectedRef.current) {
        addToast({
          type: 'success',
          title: 'Connection Restored',
          message: 'You are back online.',
          duration: 3000,
        });
      }
    } else {
      wsEverDisconnectedRef.current = true;
      addToast({
        type: 'warning',
        title: 'Connection Interrupted',
        message: 'Attempting to reconnect...',
        duration: 0, // persistent until reconnected
      });
    }
  }, [addToast]);

  usePortalWebSocket({
    customerId: customerSession?.id || 0,
    wssSign: customerSession?.wss_sign || '',
    onCallEnd: (data) => {
      logger.info('Call ended by Portal', { endBy: data.end_by });
      handleEndCall();
    },
    onBasketUpdated: () => {
      logger.info('Basket updated, refreshing cart');
      cart.refetch();
    },
    onWishlistUpdated: () => {
      logger.info('Wishlist updated, refreshing');
      wishlist.refetch();
    },
    onConnectionChange: handleWsConnectionChange,
    onSellerAssigned: (data) => {
      logger.info('Seller assigned via WebSocket', { callId: data.call_id, sellerId: data.seller.id });
      setCurrentSeller({
        id: data.seller.id,
        full_name: data.seller.full_name,
        image: data.seller.image,
      });
    },
    // The advisor's own switches between video, audio and chat are not narrated to
    // the customer: they change nothing on this side, and a card for each one is
    // noise in the middle of a consultation. The customer's own mode still goes to
    // Portal (useModeReporter) — that is what the advisor's screen shows.
    onParticipantModeChanged: (data) => {
      logger.info('Advisor mode changed', { mode: data.mode, participant: data.participant_type });
    },
  });

  // Reconnection logic
  const reconnectServices = useCallback(async () => {
    // A reconnect is a fresh capture attempt, so anything the customer settled
    // earlier stops applying: if the microphone has been taken by another app in
    // the meantime, that failure must reach them rather than being swallowed as
    // "already dealt with".
    captureErrorSettledRef.current = false;

    if (currentMode !== 'chat') {
      const videoConfig: VideoProviderConfig = {
        video: currentMode === 'video',
        audio: true,
      };
      await video.connect(credentials.videoToken, credentials.roomName, videoConfig);
    }
    await chat.connect(credentials.chatToken, credentials.conversationSid);
  }, [currentMode, credentials, video, chat]);

  const reconnection = useReconnection(reconnectServices, {
    maxAttempts: MAX_RECONNECT_ATTEMPTS,
    delayMs: 2000,
    backoffMultiplier: 2,
  });

  // Derive connection status from state (no setState in effect)
  // In chat-only mode, video is never connected by design — exclude it from the "connected" gate.
  const videoRequired = currentMode !== 'chat';
  const connectionStatus = reconnection.isReconnecting
    ? 'reconnecting'
    : video.isConnecting || chat.isConnecting
    ? 'connecting'
    : (videoRequired ? video.isConnected : true) && chat.isConnected
    ? 'connected'
    : video.error || chat.error
    ? 'failed'
    : 'connecting';

  // Install Twilio diagnostic logger when debug toggle is active (idempotent no-op when OFF)
  useEffect(() => {
    initTwilioDiag();
  }, []);

  // Unread-message indicator: set when a new remote message arrives while chat is not
  // the active mode; cleared whenever chat becomes active. prevChatMsgCountRef tracks
  // the last observed message-count so we only react to growth (new arrival), not
  // re-renders caused by the same list.
  useEffect(() => {
    if (currentMode === 'chat') {
      setChatHasUnread(false);
      prevChatMsgCountRef.current = chat.messages.length;
      return;
    }
    if (chat.messages.length > prevChatMsgCountRef.current) {
      const last = chat.messages[chat.messages.length - 1];
      if (last && !last.isLocal) {
        setChatHasUnread(true);
      }
    }
    prevChatMsgCountRef.current = chat.messages.length;
  }, [chat.messages, currentMode]);

  // IB-004 / Stream F: route advisor product-suggestion messages to the soft-focus
  // panel WITHOUT touching currentMode (keeps A/V live). We derive from chat.messages
  // — which already mirrors Twilio's `messageAdded` stream — reusing normalizeProducts.
  //
  // The baseline pass is gated on chat.historyLoaded (NOT on messages.length): the
  // provider flips historyLoaded true in the same update that publishes the initial
  // getMessages() batch, before the messageAdded listener attaches. Gating on
  // "first non-empty" would be wrong at both ends — on a re-join the history batch
  // would pulse for old products, and on a fresh (empty-history) call the FIRST live
  // suggestion would be swallowed as baseline. With historyLoaded:
  //   • baseline pass = whatever exists at history-load (history on re-join, [] on a
  //     fresh call) → seeded into the list silently, no pulse / auto-open;
  //   • every later messageAdded = live → add to list, set latest, auto-open, pulse.
  // Only remote (advisor) messages drive the focus — the client's own shares don't.
  useEffect(() => {
    if (!chat.historyLoaded) return;

    const collectProducts = (msg: (typeof chat.messages)[number]): Product[] => {
      if (msg.isLocal || !msg.attributes) return [];
      const items = normalizeProducts(msg.attributes);
      if (!items || items.length === 0) return [];
      return items
        .map(chatProductToProduct)
        .filter((p): p is Product => p !== null);
    };

    if (!suggestionsBaselinedRef.current) {
      suggestionsBaselinedRef.current = true;
      const seeded: Product[] = [];
      for (const msg of chat.messages) {
        processedSuggestionIdsRef.current.add(msg.id);
        seeded.push(...collectProducts(msg));
      }
      if (seeded.length > 0) {
        setSuggestedProducts((prev) => mergeProductsById(prev, seeded));
      }
      return;
    }

    let latest: ChatProductItem | null = null;
    const additions: Product[] = [];
    for (const msg of chat.messages) {
      if (processedSuggestionIdsRef.current.has(msg.id)) continue;
      processedSuggestionIdsRef.current.add(msg.id);
      if (msg.isLocal || !msg.attributes) continue;
      const items = normalizeProducts(msg.attributes);
      if (!items || items.length === 0) continue;
      additions.push(...items.map(chatProductToProduct).filter((p): p is Product => p !== null));
      latest = items[items.length - 1];
    }

    if (additions.length > 0) {
      setSuggestedProducts((prev) => mergeProductsById(prev, additions));
    }
    if (latest) {
      setLatestSuggestion(latest);
      setIsSuggestionExpanded(true);
      setShowProducts(true); // ensure the sidebar (and focus card) is visible
      setSuggestionPulseKey((k) => k + 1);
      logger.info('Product suggestion received', { id: latest.id, name: latest.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.messages, chat.historyLoaded]);

  // Join whatever this consultation still needs on mount.
  //
  // Deliberately not symmetrical: there is no teardown here. The session is shared
  // with TransitionScreen, so disconnecting on unmount would drop the room every
  // time the screen changes — and that unmount happens right after the transition
  // screen joined, which is what made the customer's video blink out on the
  // seller's side. The call ends through handleEndCall, which disconnects both legs
  // explicitly; closing the tab tears the provider down with the App tree.
  useEffect(() => {
    if (env.demoMode) {
      // In demo mode, only connect chat (DemoChatProvider handles it locally, no Twilio)
      chat.connect(credentials.chatToken, credentials.conversationSid);
      return;
    }

    const connectServices = async () => {
      try {
        // The room is usually already open: TransitionScreen joined it, on the same
        // provider, with the tracks this mode needs. Only the paths that skip that
        // screen — resume (sessionResume) and a connect that failed there — still
        // have to open it. Chat needs no room at all; handleModeChange creates one
        // lazily if the customer switches to voice or video later.
        if (initialMode !== 'chat' && !video.isConnected && !video.isConnecting) {
          const videoConfig: VideoProviderConfig = {
            video: initialMode === 'video',
            audio: true,
          };
          await video.connect(credentials.videoToken, credentials.roomName, videoConfig);
        }

        // Connect chat
        await chat.connect(credentials.chatToken, credentials.conversationSid);
      } catch (error) {
        logger.error('Failed to connect', { error });
        // Toast will be shown by video.error effect when state updates
      }
    };

    connectServices();
    // video and chat are the shared session — reading them here must not re-run the
    // join. Only credentials/initialMode should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials, initialMode]);

  // Publish the effective mode on every change (switch, degradation, rollback), so the
  // resume record App keeps always matches what the customer is actually looking at.
  useEffect(() => {
    onModeChange?.(currentMode);
  }, [currentMode, onModeChange]);

  // DEBUG: log connection state transitions to help diagnose chat-to-video switch issues
  useEffect(() => {
    logger.info('[conn-state] change', {
      connectionStatus,
      videoIsConnected: video.isConnected,
      videoIsConnecting: video.isConnecting,
      videoError: video.error ? { type: video.error.type, message: video.error.message } : null,
      chatIsConnected: chat.isConnected,
      chatIsConnecting: chat.isConnecting,
      currentMode,
    });
  }, [connectionStatus, video.isConnected, video.isConnecting, video.error, chat.isConnected, chat.isConnecting, currentMode]);

  // Auto-reconnect on connection loss (only when NOT already reconnecting, skip in demo mode)
  useEffect(() => {
    if (!env.demoMode && connectionStatus === 'failed' && !reconnection.isReconnecting) {
      // Local media-capture failures (device-level) must NOT trigger network reconnect.
      // They are handled by MediaErrorPrompt instead.
      if (video.error?.isLocalCapture) {
        if (!captureErrorSettledRef.current) setShowMediaErrorPrompt(true);
        return;
      }
      // Only auto-reconnect if the error is retryable
      if (!video.error || video.error.retryable) {
        reconnection.retry();
      }
    }
  }, [connectionStatus, reconnection, video.error]);

  // Network quality toast — show once per session; visual indicator provides ongoing feedback
  const nqToastShownRef = useRef(false);
  const isEndingRef = useRef(false);
  // Snapshot of camera/mic state taken when the user enters chat mode, so the
  // exit transition restores their explicit mute/unmute choice instead of
  // unconditionally re-enabling tracks. Cleared after a successful restore.
  const tracksBeforeChat = useRef<{ mic: boolean } | null>(null);
  useEffect(() => {
    if (networkQuality.isLow && !nqToastShownRef.current) {
      nqToastShownRef.current = true;
      addToast({
        type: 'warning',
        title: 'Poor Connection Quality',
        message: 'Your network connection is degraded. Video quality may be affected.',
      });
    }
  }, [networkQuality.isLow, addToast]);

  // Video/audio error toasts
  useEffect(() => {
    if (video.error) {
      // Local media-capture failures surface via MediaErrorPrompt, not the reconnect path.
      // Show a toast only for non-local errors (network/unknown); for local-capture errors
      // the prompt itself carries the human-readable message.
      if (video.error.isLocalCapture) {
        if (!captureErrorSettledRef.current) setShowMediaErrorPrompt(true);
        return;
      }
      addToast({
        type: 'error',
        title: getMediaErrorTitle(video.error.type, video.error.requested),
        message: video.error.message,
      });
      // Only retry if the error is retryable
      if (video.error.retryable && !reconnection.isReconnecting) {
        reconnection.retry();
      }
    }
  }, [video.error, addToast, reconnection]);

  useEffect(() => {
    if (chat.error) {
      addToast({
        type: 'error',
        title: 'Chat Connection Failed',
        message: chat.error,
      });
    }
  }, [chat.error, addToast]);

  // Handle mode change — manages media tracks based on selected mode.
  // Resolves false when the lazy connect behind the target mode failed, true
  // otherwise. "True" means the room the mode needs exists — it does not promise a
  // completed track transition, which surfaces asynchronously through video.error.
  // Callers use it to pick the next fallback, and that is the question it answers.
  const handleModeChange = useCallback(async (mode: CommunicationMode): Promise<boolean> => {
    logger.info('[mode-switch] enter', {
      targetMode: mode,
      currentMode,
      isConnected: video.isConnected,
      isConnecting: video.isConnecting,
      isCameraOn: video.isCameraOn,
      isMicOn: video.isMicOn,
      demoMode: env.demoMode,
    });

    // Remembered before the optimistic switch below, so a failed lazy connect can
    // put the interface back where the customer actually still is.
    const previousMode = currentMode;

    setCurrentMode(mode);
    setIsChatOpen(mode === 'chat');

    if (mode === 'chat') {
      logger.info('[mode-switch] -> chat: turning off tracks');
      // Snapshot pre-chat state so exit transition can restore exactly what
      // the user had — including an explicit mute. Without this, the audio/video
      // arms below would unconditionally re-enable mic and override the user.
      tracksBeforeChat.current = { mic: video.isMicOn };
      if (video.isCameraOn) video.toggleCamera();
      if (video.isMicOn) video.toggleMic();
      // Chat needs no room, so nothing is pending any more. Leaving a stale target
      // here would let a later "Try again" re-attempt a mode the customer has since
      // moved away from.
      pendingModeRef.current = null;
      pendingToggleModeRef.current = null;
      return true;
    }

    // Q1, hole B: leaving chat is the moment this customer starts publishing a
    // microphone into a room that is already being recorded. The question is not
    // re-asked here — that is the customer's decision of 2026-08-28, and the
    // recording rules Portal builds read "no answer" as "no", so the track is
    // excluded either way. What must not happen is this passing in silence: an
    // unanswered consent arriving at this line is how hole B opened, and the log is
    // the only thing that would say so next time.
    if (recordingEnabled && recordingConsent === null) {
      logger.warn('[Q1] mode switch out of chat with no recording-consent answer', {
        targetMode: mode,
      });
    }

    // audio or video mode requires a connected Twilio video room. If the user
    // joined in chat-only mode, the mount effect skipped video.connect — establish
    // it now on first switch. Subsequent switches between audio/video just toggle tracks.
    if (!env.demoMode && !video.isConnected && !video.isConnecting) {
      logger.info('[mode-switch] lazy-connecting Twilio room', {
        targetMode: mode,
        roomName: credentials.roomName,
      });
      pendingModeRef.current = mode;
      try {
        await video.connect(credentials.videoToken, credentials.roomName, {
          video: mode === 'video',
          audio: true,
        });
        logger.info('[mode-switch] lazy connect resolved');
        pendingModeRef.current = null;
        // Initial tracks match the target mode on first connect — no further toggling needed.
        return true;
      } catch (error) {
        logger.error('[mode-switch] lazy connect failed', { error: String(error) });
        // Roll the interface back. `currentMode` was moved optimistically above; leaving
        // it on video/audio paints a live-call layout over a room that was never
        // created — the state the customer could only escape by reloading the page.
        setCurrentMode(previousMode);
        setIsChatOpen(previousMode === 'chat');
        return false;
      }
    }

    logger.info('[mode-switch] room already established — toggling tracks', {
      targetMode: mode,
    });

    // Camera follows mode strictly (no user-facing camera toggle). Mic is
    // user-controlled — only restore the pre-chat snapshot when leaving chat;
    // direct audio↔video switches preserve current mic state.
    const isLeavingChat = currentMode === 'chat' && tracksBeforeChat.current !== null;
    const snap = tracksBeforeChat.current;

    const targetCamera = mode === 'video';
    const targetMic = isLeavingChat ? snap!.mic : video.isMicOn;

    // Switching a device on may be the first time the browser asks about it (a
    // voice consultation never asked for the camera). If that is refused, the
    // provider has already raised the modal through video.error; what is left is
    // not to leave the label on a mode whose device does not exist — the same
    // rollback as a failed lazy connect. The camera goes first and alone, so a
    // refusal leaves the microphone and the pre-chat snapshot untouched.
    // The camera request goes out before anything in this branch is awaited, which
    // keeps it inside the click that started it.
    const rollBackToggle = () => {
      pendingToggleModeRef.current = mode;
      setCurrentMode(previousMode);
      setIsChatOpen(previousMode === 'chat');
      return false;
    };
    if (video.isCameraOn !== targetCamera && (await video.toggleCamera()) === false) {
      return rollBackToggle();
    }
    if (video.isMicOn !== targetMic && (await video.toggleMic()) === false) {
      return rollBackToggle();
    }

    pendingToggleModeRef.current = null;
    if (isLeavingChat) tracksBeforeChat.current = null;
    return true;
  }, [video, credentials, currentMode, recordingConsent, recordingEnabled]);

  // ModeSwitcher entry point. An explicit request from the customer, so a
  // previously-settled capture failure becomes fair game to surface again.
  const handleModeSelect = useCallback((mode: CommunicationMode) => {
    captureErrorSettledRef.current = false;
    void handleModeChange(mode);
  }, [handleModeChange]);

  // Handle graceful degradation
  const handleDegradation = useCallback((newMode: CommunicationMode) => {
    if (newMode === 'audio') {
      // Disable video track using the shared session's API
      if (video.isCameraOn) {
        video.toggleCamera();
      }
      setCurrentMode('audio');
    } else if (newMode === 'chat') {
      // Disconnect video entirely, keep chat
      video.disconnect();
      setCurrentMode('chat');
      setIsChatOpen(true);
    }

    setShowDegradation(false);
    addToast({
      type: 'info',
      title: `Switched to ${newMode === 'audio' ? 'Audio' : 'Chat'}`,
      message: `Continuing your consultation in ${newMode} mode.`,
    });
  }, [video, addToast]);

  // Handle switch mode from reconnection overlay
  const handleSwitchFromOverlay = useCallback(() => {
    const suggested = getSuggestedMode(currentMode);
    if (suggested) {
      reconnection.reset();
      setShowDegradation(true);
    }
  }, [currentMode, reconnection]);

  // MediaErrorPrompt: "Continue without video" — fall to audio, or chat if mic also unavailable
  const handleMediaErrorContinueWithoutVideo = useCallback(async () => {
    setShowMediaErrorPrompt(false);
    // Set before the fallback attempts, not after: their own failure would otherwise
    // re-arm the modal from the video.error effect before we got here.
    captureErrorSettledRef.current = true;

    // The microphone is what failed, so there is no audio mode to fall back to —
    // the modal says "Continue in chat" for exactly this case. Trying audio first
    // would only re-open the same failing device before landing here anyway.
    if (video.error?.requested === 'microphone') {
      await handleModeChange('chat');
      addToast({
        type: 'info',
        title: 'Continuing in Chat',
        message: 'Your microphone is unavailable. You can keep chatting with your specialist.',
      });
      return;
    }

    // Pre-room failure: there is no session to degrade — the room the customer asked
    // for was never created. handleDegradation would flip the label to "audio" over a
    // room that does not exist, which is exactly the dead end from the demo. Re-attempt
    // the connect with video off instead; chat is the floor if audio is out too.
    if (!video.isConnected && pendingModeRef.current) {
      if (await handleModeChange('audio')) {
        addToast({
          type: 'info',
          title: 'Switched to Audio',
          message: 'Continuing your consultation in audio mode.',
        });
        return;
      }
      await handleModeChange('chat');
      addToast({
        type: 'info',
        title: 'Continuing in Chat',
        message: 'Your camera and microphone are unavailable. You can keep chatting with your specialist.',
      });
      return;
    }

    // In-room refusal of the camera: the switch already rolled back, and the voice
    // call it came from is still running. "Continue without video" means exactly
    // that — degrading from the rolled-back mode would drop a working voice call
    // to chat.
    if (video.isConnected && pendingToggleModeRef.current) {
      pendingToggleModeRef.current = null;
      if (currentMode !== 'audio' && !(await handleModeChange('audio'))) {
        await handleModeChange('chat');
        return;
      }
      addToast({
        type: 'info',
        title: 'Continuing in Audio',
        message: 'Your camera is unavailable. Your specialist can still hear you.',
      });
      return;
    }

    const suggestedFallback = getSuggestedMode(currentMode) ?? 'chat';
    handleDegradation(suggestedFallback);
  }, [currentMode, handleDegradation, handleModeChange, video, addToast]);

  // MediaErrorPrompt: "Change camera" — switch to a different device if room exists,
  // otherwise re-attempt connect with the newly-selected device
  const handleMediaErrorChangeDevice = useCallback(async () => {
    setShowMediaErrorPrompt(false);
    captureErrorSettledRef.current = false;
    const newDeviceId = deviceSelection.selectedCamera;
    if (!newDeviceId) return;
    if (video.isConnected) {
      try {
        await video.switchDevice('video', newDeviceId);
      } catch (err) {
        logger.error('MediaErrorPrompt changeDevice: switchDevice failed', { error: err });
        setShowMediaErrorPrompt(true);
      }
    } else {
      // Pre-room: re-attempt connect with the newly selected camera. The failed
      // attempt rolled `currentMode` back, so restore the mode the customer asked
      // for on success — otherwise a live room sits under a "chat" label.
      const target = pendingModeRef.current ?? 'video';
      try {
        const config: VideoProviderConfig = {
          video: target === 'video',
          videoConstraints:
            target === 'video' ? { deviceId: { exact: newDeviceId } } : undefined,
          audio: true,
        };
        await video.connect(credentials.videoToken, credentials.roomName, config);
        pendingModeRef.current = null;
        setCurrentMode(target);
        setIsChatOpen(false);
      } catch {
        setShowMediaErrorPrompt(true);
      }
    }
  }, [deviceSelection.selectedCamera, video, credentials]);

  // MediaErrorPrompt: "Try again" — re-attempt capture with current selection
  const handleMediaErrorRetry = useCallback(async () => {
    setShowMediaErrorPrompt(false);
    captureErrorSettledRef.current = false;

    // Pre-room failure: the lazy connect behind a mode switch never created a room,
    // so there is nothing to re-establish. reconnectServices() is the wrong tool here
    // twice over — it would re-join the live chat conversation as well, and it reads
    // `currentMode`, which the failure has already rolled back. Re-run the mode switch
    // instead: that is the action that failed. Mirrors the branch that
    // handleMediaErrorChangeDevice already had.
    const pending = pendingModeRef.current;
    if (!video.isConnected && pending) {
      await handleModeChange(pending);
      return;
    }

    // In-room device refusal: the room is live, so ask for the device again by
    // repeating the switch. A reconnect here would re-join a room we are in.
    const pendingToggle = pendingToggleModeRef.current;
    if (video.isConnected && pendingToggle) {
      await handleModeChange(pendingToggle);
      return;
    }

    try {
      await reconnectServices();
    } catch {
      setShowMediaErrorPrompt(true);
    }
  }, [reconnectServices, video, handleModeChange]);

  const captureRetry = useCaptureRetry({
    error: video.error,
    isVisible: showMediaErrorPrompt,
    onRetry: handleMediaErrorRetry,
  });

  // Handle end call request (show confirmation dialog)
  const handleEndCallRequest = useCallback(() => {
    setShowEndCallConfirm(true);
  }, []);

  // Handle end call
  // Notify Portal via /stop (covers the full scheduled-call lifecycle per SPEC-055) BEFORE local teardown.
  // Portal failure must NEVER block teardown or navigation — the user always reaches ThankYouScreen.
  const handleEndCall = useCallback(async () => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;

    if (auth && callId) {
      try {
        await apiClient.stopCall(callId, auth);
      } catch (err) {
        // Intentionally swallow — widgetRequest already captures 5xx/network to Sentry.
        // Do not trap the customer in the consultation if Portal notify fails.
        logger.error('Portal end-call notify failed; continuing local teardown', { error: err });
      }
    }
    video.disconnect();
    chat.disconnect();
    onEndCall();
  }, [auth, callId, video, chat, onEndCall]);

  // SPEC-053: Async Reserve with variant resolution — fetches product detail to get variants,
  // then either opens PDP (multi-variant), adds to cart (single in-stock variant),
  // or shows a toast (out-of-stock / unavailable). Per-card loading via reservingProductId.
  const handleReserve = useCallback(async (product: Product) => {
    if (!auth) return; // no session yet
    if (reservingProductId !== null) return; // ignore concurrent clicks on different cards

    setReservingProductId(product.id);
    try {
      const detail = await apiClient.getProductDetail(product.id, auth);
      const variants = detail.response?.product_variants ?? [];

      if (variants.length === 0) {
        addToast({ type: 'error', title: 'Unavailable', message: 'This product is unavailable' });
        return;
      }

      if (variants.length > 1) {
        // Close other overlays, then open PDP for variant selection
        if (cart.isOpen) cart.toggleCart();
        if (wishlist.isOpen) wishlist.toggleWishlist();
        handleProductClick(product);
        return;
      }

      // Single variant path
      const variant = variants[0];
      if (variant.inventory <= 0 && !variant.add_to_cart_when_out_of_stock) {
        addToast({ type: 'info', title: 'Out of Stock', message: 'This item is out of stock' });
        return;
      }

      await cart.addItem(product, { variantId: variant.id });
    } catch (err) {
      logger.error('handleReserve failed', { productId: product.id, error: err });
      addToast({ type: 'error', title: 'Could Not Reserve', message: 'Could not reserve item, please try again' });
    } finally {
      setReservingProductId(null);
    }
  }, [reservingProductId, auth, cart, wishlist, addToast, handleProductClick]);

  // SPEC-053: Wishlist Reserve bypass — uses item.productVariant.id directly,
  // no getProductDetail call needed since wishlist items already carry variant info.
  const handleReserveFromWishlist = useCallback(async (item: WishlistItem) => {
    try {
      await cart.addItem(item.product, { variantId: item.productVariant.id });
    } catch (err) {
      logger.error('handleReserveFromWishlist failed', { itemId: item.id, error: err });
      addToast({ type: 'error', title: 'Could Not Reserve', message: 'Could not reserve item, please try again' });
    }
  }, [cart, addToast]);

  // Handoff subtask #14: remove-from-bag action on wishlist cards.
  // Resolve basket product id from cart.items (keyed by productVariant.id) and
  // call cart.removeItem. Silent no-op if the variant is no longer in cart
  // (race with concurrent cart mutation).
  const handleRemoveFromBag = useCallback(async (item: WishlistItem) => {
    const cartItem = cart.items.find((ci) => ci.productVariant.id === item.productVariant.id);
    if (!cartItem) return;
    try {
      await cart.removeItem(cartItem.id);
    } catch (err) {
      logger.error('handleRemoveFromBag failed', { itemId: item.id, basketProductId: cartItem.id, error: err });
      addToast({ type: 'error', title: 'Could Not Remove', message: 'Could not remove item from bag, please try again' });
    }
  }, [cart, addToast]);

  const suggestedMode = getSuggestedMode(currentMode);

  // Determine whether to show ConnectionState vs ReconnectionOverlay
  const showConnectionState = !env.demoMode && connectionStatus === 'connecting' && !reconnection.isReconnecting;
  const showReconnectionOverlay = reconnection.isReconnecting || (reconnection.attempt >= MAX_RECONNECT_ATTEMPTS && connectionStatus === 'failed');

  // Suppress unused variable warnings — refetchProducts available for future use
  void refetchProducts;

  // Null-safe seller values derived from local state (updated via WebSocket push).
  // Until the seller is assigned, fall back to the organization logo instead of an
  // initial letter — the customer sees the boutique's brand while waiting.
  const organizationImage = organization?.image;
  const sellerBound = !!(currentSeller || video.remoteParticipants.length > 0);
  const advisorName = sellerBound ? (currentSeller?.full_name ?? undefined) : undefined;
  const advisorTitle = currentSeller?.title;
  const advisorImage = currentSeller?.image ?? organizationImage;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-screen overflow-hidden bg-brand-navy flex flex-col"
    >
      {/* Connection status indicator (initial connection only) */}
      <AnimatePresence>
        {showConnectionState && <ConnectionState status={connectionStatus} />}
      </AnimatePresence>

      {/* "Waiting for advisor to join..." banner — visible while seller not yet assigned.
          Also hide when a Twilio remote participant is already present: the seller is
          demonstrably in the room even if the Portal WS `seller_assigned` event never
          fired (e.g. seller joined before buyer established WS). */}
      <AnimatePresence>
        {!currentSeller && video.remoteParticipants.length === 0 && (
          <motion.div
            key="waiting-for-advisor"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="bg-brand-gold-light/10 border-b border-brand-gold-light/30 px-6 py-3 text-center"
          >
            <p className="text-brand-gold-light text-sm tracking-wide font-sans">
              Waiting for advisor to join…
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast notifications */}
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      {/* Reconnection overlay */}
      <ReconnectionOverlay
        isVisible={showReconnectionOverlay}
        attempt={reconnection.attempt}
        maxAttempts={MAX_RECONNECT_ATTEMPTS}
        onRetry={reconnection.retry}
        onSwitchMode={suggestedMode ? handleSwitchFromOverlay : undefined}
        suggestedMode={suggestedMode ?? undefined}
      />

      {/* Degradation prompt */}
      {suggestedMode && (
        <DegradationPrompt
          isVisible={showDegradation}
          currentMode={currentMode}
          suggestedMode={suggestedMode}
          onAccept={() => handleDegradation(suggestedMode)}
          onDismiss={() => {
            setShowDegradation(false);
            reconnection.retry();
          }}
        />
      )}

      {/* Media error prompt — shown for local device-capture failures (not network errors) */}
      {video.error && (
        <MediaErrorPrompt
          isVisible={showMediaErrorPrompt}
          error={video.error}
          onContinueWithoutVideo={handleMediaErrorContinueWithoutVideo}
          onChangeDevice={handleMediaErrorChangeDevice}
          onRetry={captureRetry.blocked ? undefined : captureRetry.retry}
          canChangeDevice={deviceSelection.cameras.length > 1}
        />
      )}

      {/* End call confirmation */}
      <EndCallConfirmation
        isVisible={showEndCallConfirm}
        onConfirm={handleEndCall}
        onCancel={() => setShowEndCallConfirm(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Video area */}
        <div className="flex-1 relative bg-brand-dark flex items-center justify-center p-6 lg:p-10">
          {currentMode !== 'chat' ? (
            <VideoPanel
              remoteVideoRef={video.remoteVideoRef}
              remoteAudioRef={video.remoteAudioRef}
              localVideoRef={video.localVideoRef}
              advisorName={advisorName}
              advisorTitle={advisorTitle}
              organizationImage={organizationImage}
              isCameraOn={video.isCameraOn}
              showPiP={currentMode === 'video'}
              networkQualityLevel={networkQuality.localLevel}
              sellerConnected={video.remoteParticipants.length > 0}
              isMicOn={video.isMicOn}
              onToggleMic={video.toggleMic}
            />
          ) : (
            <div className="w-full h-full bg-brand-dark flex items-center justify-center">
              <div className="text-center">
                {/* Mirror VideoPanel's waiting-state circle (bordered wrapper +
                    inner object-contain img). Matching styles here is what keeps
                    the mode-switch DOM swap invisible under the ChatOverlay fade —
                    any size/border difference surfaces as a visible "jump" of
                    the logo during the 0.6s transition. */}
                <div className="w-24 h-24 rounded-full border-2 border-brand-gold/60 bg-brand-gold/10 overflow-hidden flex items-center justify-center mx-auto mb-4">
                  {advisorImage ? (
                    <img
                      src={advisorImage}
                      alt={advisorName ?? 'Your specialist'}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-3xl font-serif text-brand-gold">
                      {advisorName ? advisorName.charAt(0).toUpperCase() : ''}
                    </span>
                  )}
                </div>
                {advisorName && (
                  <h2 className="text-white font-serif text-xl">{advisorName}</h2>
                )}
                <p className="text-brand-gold-light">Chat mode active</p>
              </div>
            </div>
          )}

          {/* Recording indicator (SPEC-026, split in Q1) */}
          {recordingEnabled && (
            <RecordingIndicator
              voiceIncluded={recordingConsent === true}
              className="absolute top-4 left-4 z-10"
            />
          )}

          {/* Chat overlay (positioned inside video area) */}
          <ChatOverlay
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            messages={chat.messages}
            onSendMessage={chat.sendMessage}
            advisorName={advisorName}
            advisorTitle={advisorTitle}
            advisorImage={advisorImage}
            onProductClick={handleChatProductClick}
          />
        </div>

        {/* Products panel (mobile: full-width capped at 40vh, desktop: 420px sidebar) */}
        <AnimatePresence>
          {showProducts && (
            <motion.div
              key="products-panel"
              initial={{ x: 420, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 420, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
              className="w-full lg:w-[420px] max-h-[40vh] lg:max-h-full bg-brand-cream flex flex-col overflow-hidden border-l border-brand-gold/20 relative"
            >
              {/* IB-004 / Stream F: advisor product-suggestion soft-focus (in-call, A/V stays live) */}
              <ProductSuggestionFocus
                suggestion={latestSuggestion}
                isExpanded={isSuggestionExpanded}
                onToggle={handleSuggestionToggle}
                onViewDetails={handleSuggestionViewDetails}
                pulseKey={suggestionPulseKey}
                advisorName={advisorName}
              />

              <div className="flex-1 min-h-0">
                <ProductsPanel
                  products={displayProducts}
                  cartItems={cart.items}
                  onReserve={handleReserve}
                  onRemoveFromCart={cart.removeByProductId}
                  isLoading={productsLoading}
                  cartCount={cart.totalItems}
                  onCartClick={handleCartClick}
                  onClose={() => setShowProducts(false)}
                  isWishlisted={(pid) => wishlist.items.some(i => i.product.id === pid)}
                  onToggleFavorite={handleProductsPanelToggleFavorite}
                  wishlistCount={wishlist.totalItems}
                  onWishlistClick={handleWishlistClick}
                  onProductClick={handleProductClick}
                  reservingProductId={reservingProductId}
                />
              </div>

              {/* Cart overlay (positioned inside products panel area) */}
              <CartOverlay
                isOpen={cart.isOpen}
                onClose={cart.toggleCart}
                items={cart.items}
                total={cart.total}
                isLoading={cart.isLoading}
                onUpdateQuantity={cart.updateQuantity}
                onRemoveItem={cart.removeItem}
                onProductClick={handleOverlayItemClick}
              />

              {/* Wishlist overlay (SPEC-033, positioned inside products panel area) */}
              <WishlistOverlay
                isOpen={wishlist.isOpen}
                onClose={wishlist.toggleWishlist}
                items={wishlist.items}
                isLoading={wishlist.isLoading}
                onRemoveItem={wishlist.removeItem}
                onReserveItem={handleReserveFromWishlist}
                onRemoveFromBag={handleRemoveFromBag}
                onProductClick={handleOverlayItemClick}
                cartVariantIds={cartVariantIdSet}
              />

              {/* Product Detail overlay (SPEC-034, positioned inside products panel area) */}
              <ProductDetailOverlay
                isOpen={selectedProductId !== null}
                onClose={handleProductDetailClose}
                productId={selectedProductId}
                product={productDetail.product}
                isLoading={productDetail.isLoading}
                error={productDetail.error}
                onAddToBag={handlePdpAddToBag}
                onAddComboBag={handlePdpAddComboBag}
                onRemoveFromBag={(id) => cart.removeItem(id)}
                onToggleFavorite={handlePdpToggleFavorite}
                isInCart={(pid, vid) => cart.items.some(item => item.product.id === pid && item.productVariant.id === vid)}
                isWishlisted={wishlist.isWishlisted}
                findInCart={cart.findInCart}
                onCartQuantityChange={handlePdpQuantityChange}
                onRetry={() => selectedProductId && productDetail.fetchProduct(selectedProductId)}
                onShareProduct={handlePdpShare}
                initialVariantId={pdpInitialVariantId}
                initialVariantRequest={pdpVariantRequest}
                auth={auth ?? undefined}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Controls — layout: End (left) | ModeSwitcher (center) | Hide (right) */}
      <div className="bg-brand-dark border-t border-brand-gold/20 px-6 py-5">
        <div className="max-w-7xl mx-auto flex items-center">
          <div className="flex-1 flex justify-start">
            <button
              onClick={handleEndCallRequest}
              className="px-6 py-4 text-white/50 hover:text-white text-xs tracking-[0.15em] uppercase font-sans transition-colors duration-500"
            >
              End Consultation
            </button>
          </div>

          <div className="flex-1 flex justify-center">
            <ModeSwitcher
              currentMode={currentMode}
              onModeChange={handleModeSelect}
              chatHasUnread={chatHasUnread}
            />
          </div>

          <div className="flex-1 flex justify-end">
            <button
              onClick={() => setShowProducts((prev) => !prev)}
              className="hidden lg:block px-6 py-4 bg-brand-gold/20 text-brand-gold text-xs tracking-[0.15em] uppercase font-sans hover:bg-brand-gold/30 transition-all duration-500 rounded-sm"
            >
              {showProducts ? 'Hide Products' : 'Show Products'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
